import { useSignIn } from '@clerk/clerk-react';
import { isClerkAPIResponseError } from '@clerk/clerk-react/errors';
import type { ClerkAPIError } from '@clerk/types';
import { useForm } from '@tanstack/react-form';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '../../components/buttons/button';
import { ErrorField } from '../../components/inputs/error-field';
import { PasswordField } from '../../components/inputs/password-field';
import { TextField } from '../../components/inputs/text-field';
import { passwordValidator, userSchema } from '../../entity/api/user';
import { BadRequestError } from '../../error';
import { addTrackCrumb, trackError } from '../../error/tracking';
import { captureException } from '../../logger/capture-exception';
// import { signOut } from '../../sdk/authorization';
import { tGlobal, useTNamespace } from '../../services/translation';
import { tanstackLocationWithUtm } from '../../shared/utm';
import { parseFirstZodError } from '../../shared/validation';
import { AuthBody } from './-authbody';
import { GoogleOauthSign } from './-google-oauth-sign';
import { OrSeparator } from './-or-separator';

export const Route = createFileRoute('/auth/signin')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/auth/signin',
      level: 'info',
    });
    addTrackCrumb({
      category: 'auth',
      message: 'inside signin',
      level: 'info',
    });

    // TODO: do we still need the sign out? maybe we take the error and redirect
    // signOut().catch(function (_err) {
    //   // NOTE: this is just a safe case, we can ignore the error
    // });
  },
  // eslint-disable-next-line no-use-before-define
  component: Index,
});

async function sendSignIn(
  signIn: ReturnType<typeof useSignIn>['signIn'],
  setActive: ReturnType<typeof useSignIn>['setActive'],
  data: {
    value: {
      email: string;
      password: string;
    };
  }
) {
  if (signIn == null || setActive == null) {
    return;
  }

  addTrackCrumb({
    category: 'auth',
    message: 'signing in',
    level: 'info',
  });

  const signInAttempt = await signIn.create({
    identifier: data.value.email,
    password: data.value.password,
  });

  // If verification was completed, set the session to active
  // and redirect the user
  if (signInAttempt.status === 'complete') {
    await setActive({ session: signInAttempt.createdSessionId });
    return;
  }

  // If the status is not complete, check why. User may need to
  // complete further steps.
  throw new BadRequestError('unable to signin', { cause: signInAttempt });
}

function Index() {
  const t = useTNamespace('auth.signin');
  const { isLoaded, signIn, setActive } = useSignIn();
  const [errors, setErrors] = useState<ClerkAPIError[]>();

  const navigate = useNavigate();
  const forgotLoc = tanstackLocationWithUtm('/auth/forgot');
  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: async function (data) {
      setErrors(undefined);
      // TODO: should clear errors on submit

      if (!isLoaded || signIn == null) {
        return;
      }

      // TODO: do we still need the sign out? maybe we take the error and redirect
      // return signOut()
      //   .then(() => sendSignIn(signIn, setActive, data))
      return sendSignIn(signIn, setActive, data)
        .then(async function () {
          // the session doesn't seem to enter straight away, lets wait
          await new Promise(function (resolve) {
            setTimeout(function () {
              resolve(undefined);
            }, 1000);
          });

          addTrackCrumb({
            category: 'auth',
            message: 'redirecting to /app',
            level: 'info',
          });

          return navigate({
            to: '/app',
            replace: true,
          });
        })
        .catch(function (err) {
          if (isClerkAPIResponseError(err)) {
            // if already signed in and an error comes, lets just
            // redirect the user, it will be handled
            const isSignedIn = (err?.errors ?? []).some(function (err) {
              let hasErr = (err.message ?? '').indexOf('already signed in') !== -1;
              hasErr = hasErr || (err.longMessage ?? '').indexOf('already signed in') !== -1;
              return hasErr;
            });
            if (isSignedIn) {
              return navigate({
                to: '/app',
                replace: true,
              });
            }

            setErrors(err.errors);
            return;
          }

          captureException(
            err,
            {
              file: 'auth/signin.tsx',
              method: 'sendSignIn',
              infoMessage: 'unable to send sign in',
            },
            trackError
          );
        });
    },
  });

  return (
    <AuthBody showSignIn={false} showDemo={true} pageTitle={t('signInToBlym')}>
      <GoogleOauthSign
        isSignup={false}
        redirectCallbackUrl="/auth/oauthcb"
        redirectUrlComplete="/auth/wait-user"
        className="mb-8"
      />
      <OrSeparator className="mb-8" />
      <form
        noValidate
        className="mb-6 flex w-full flex-col gap-4"
        onSubmit={function (e) {
          e.preventDefault();
          e.stopPropagation();

          form.handleSubmit().catch(function (err) {
            captureException(
              err,
              {
                file: 'auth/signin.tsx',
                method: 'form.handleSubmit',
                infoMessage: 'unable to send form',
              },
              trackError
            );
          });
        }}
      >
        <form.Field
          name="email"
          validators={{
            onSubmit: function (data) {
              return parseFirstZodError(userSchema.shape.email, data.value);
            },
            onChange: function (data) {
              if (data.fieldApi.form.state.isSubmitted) {
                return parseFirstZodError(userSchema.shape.email, data.value);
              }
              return '';
            },
          }}
          // eslint-disable-next-line react/no-children-prop
          children={function (field) {
            return (
              <div className="form-group">
                <TextField
                  sizeField="lg"
                  placeholder={tGlobal('email')}
                  type="email"
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={function (e) {
                    field.handleChange(e.target.value);
                  }}
                  error={field?.state?.meta?.errors}
                  autoComplete="email"
                  data-testid="form-email"
                />
              </div>
            );
          }}
        />
        <form.Field
          name="password"
          validators={{
            onSubmit: function (data) {
              return parseFirstZodError(passwordValidator, data.value);
            },
            onChange: function (data) {
              if (data.fieldApi.form.state.isSubmitted) {
                return parseFirstZodError(passwordValidator, data.value);
              }
              return '';
            },
          }}
          // eslint-disable-next-line react/no-children-prop
          children={function (field) {
            return (
              <div className="form-group">
                <PasswordField
                  sizeField="lg"
                  placeholder={tGlobal('password')}
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={function (e) {
                    field.handleChange(e.target.value);
                  }}
                  error={field?.state?.meta?.errors}
                  autoComplete="current-password"
                  data-testid="form-password"
                />
              </div>
            );
          }}
        />
        <ErrorField sizeField="lg" error={errors} />
        <form.Subscribe
          selector={function (state) {
            return [state.isSubmitting, state.isPristine];
          }}
          // eslint-disable-next-line react/no-children-prop
          children={function (data) {
            const [isSubmitting, isPristine] = data;
            return (
              <Button
                className="w-full"
                loading={isSubmitting}
                type="submit"
                disabled={isPristine}
                size="xl"
                variant="accent"
                data-testid="form-submit"
              >
                {t('signIn')}
              </Button>
            );
          }}
        />
      </form>
      <div className="mb-8 w-full text-center">
        <Link
          to={forgotLoc.to}
          search={forgotLoc.search}
          className="font-body-2-sb inline-block text-branding-accent"
        >
          {t('resetPassword')}
        </Link>
      </div>
    </AuthBody>
  );
}
